import { en } from "./catalogs/en";
import { formatMessage } from "./catalog";
import type {
  MessageArguments,
  MessageKey,
  MessageParameters,
} from "./catalog";
import { localeRegistry, resolveLocale } from "./registry";

let locale = resolveLocale(typeof navigator === "undefined" ? {} : navigator);
const subscribers = new Set<() => void>();

export const getLocale = (): string => locale;
export function subscribeLocale(listener: () => void): () => void {
  subscribers.add(listener);
  return () => {
    subscribers.delete(listener);
  };
}

/** Runtime changes update captions only; identities, world state and saves stay intact. */
export function setLocale(tag: string): void {
  const next = resolveLocale({ language: tag });
  if (next === locale) return;
  locale = next;
  subscribers.forEach((listener) => listener());
}

export function t<K extends MessageKey>(
  key: K,
  ...args: MessageArguments<K>
): string {
  return translateMessage(locale, key, args);
}

export function translateMessage<K extends MessageKey>(
  tag: string,
  key: K,
  args: MessageArguments<K>,
  registry = localeRegistry,
): string {
  const translated = registry.get(tag)?.catalog[key];
  const message = translated?.trim() ? translated : en[key];
  return formatMessage(message, args[0] ?? {});
}

export function messageLocale(key: MessageKey, tag = locale): string {
  return localeRegistry.get(tag)?.catalog[key]?.trim() ? tag : "en";
}

/** Build localizable parameters using the actual sentence locale after fallback. */
export function tMessage<K extends MessageKey>(
  key: K,
  build: (tag: string) => MessageParameters<K>,
  tag = locale,
): string {
  const actual = messageLocale(key, tag);
  const translated = localeRegistry.get(actual)?.catalog[key];
  return formatMessage(
    translated?.trim() ? translated : en[key],
    build(actual),
  );
}
